import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, Input, SearchIcon } from '@fluentui/react-northstar';
import _ from 'lodash';
import { platformService } from '../../../shared/services/platform.service';
import { appState, useSetState } from '../../../AppState';
import { RequestCardSkeleton } from '../Skeletons/RequestCardSkeleton';
import { TicketRequestCard } from '../TicketRequestCard/TicketRequestCard';
import FlashlightSVG from './../../../../svg/flashlight.svg';
import { EmptyData } from '../../../shared/components/EmptyData';
import { Toggle } from '@fluentui/react';
import { TICKET_PAGINATION } from '../../../shared/utils/constants';
import { ticketSearchFilter } from '../../../shared/utils/ticketSearchFilter';
import { themeNames } from '@fluentui/react-teams';
import { useTranslation } from 'react-i18next';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';

interface Props {
  ticketId: number;
  globalTheme: any;
}

export const EditRequestsView = ({ ticketId, globalTheme }: Props) => {
  const { t } = useTranslation();
  const api = new platformService();
  const setState = useSetState();
  const currentState = appState();
  const myRef = useRef(null)

  const observer = useRef<IntersectionObserver>();
  const [ticketsState, setTicketsState] = useState<SideViewTicketState>({ loading: false, hasMore: false, pageNumber: 1, tickets: [], totalTickets: 0, searchText: '', selectedViewId: 0 });

  const lastTicketElementRef = useCallback(
    node => {
      if (ticketsState.loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && ticketsState.hasMore)
          setTicketsState(pre => ({ ...pre, pageNumber: pre.pageNumber + 1 }));
      });

      if (node) observer.current.observe(node);

      if (myRef.current != null) myRef.current.scrollIntoViewIfNeeded(true);

    },
    [ticketsState.loading, ticketsState.hasMore]
  );

  const loadSideViewTickets = async (page: number, search?: string, closed?: boolean) => {
    closed ??= currentState.request.isToggleClosed;
    search ??= ticketsState.searchText;

    setTicketsState(pre => ({ ...pre, loading: true }));
    try {
      const expandQuery = "AffectedUsers($select=PlatformUserId),FileAttachments($filter=IsPublic eq true;$top=1;$orderby=Id desc;),Comments($filter=IsPublic eq true and IsDeleted eq false;$top=1;$orderby=Id desc;$select=Id,Body,CreatedById,IsPublic,IsDeleted),TicketCollaborators($select=PlatformUserId),TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true))";
      let filterQuery = `Closed eq ${closed} and (RequesterId eq ${currentState.currentUserId} or AffectedUsers/any(a: a/PlatformUserId eq ${currentState.currentUserId}))`
      if (search)
        filterQuery += `and ${ticketSearchFilter(search, { matchId: true, comments: 'public' })}`;

      filterQuery += `&$expand=${expandQuery}`;

      const { data } = await api.getTicketList(`?$filter=${filterQuery}&$orderby=CreatedDate desc&$top=${TICKET_PAGINATION.ITEM_PER_PAGE()}&$count=true&$skip=${(page - 1) * TICKET_PAGINATION.ITEM_PER_PAGE()}`);
      const users = await getCachedUsers(data.value.map(x => [x.RequesterId, x.AssigneeId, ...x.AffectedUsers.map(a => a.PlatformUserId), ...x.TicketCollaborators.map(a => a.PlatformUserId), ...x.Comments.map(a => a.CreatedById)]).flat().filter(y => y));
      data.value.forEach(t => {
        t.Requester = users.find(u => u.Id == t.RequesterId);
        t.Assignee = users.find(u => u.Id == t.AssigneeId);
        t.AffectedUsers?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId));
        t.TicketCollaborators?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId));
        t.Comments?.forEach(a => a.CreatedBy = users.find(u => u.Id == a.CreatedById));
      });

      const selectedTicket = data.value.find(x => x.Id == ticketId) ?? ticketsState.tickets.find(x => x.Id == ticketId);

      if (!selectedTicket) {
        let currentTicket = await api.getTicket(ticketId, `?$expand=${expandQuery}`);
        if (currentTicket?.data)
          data.value.unshift(currentTicket.data);
      }

      setTicketsState(pre => ({
        ...pre,
        loading: false,
        hasMore: data['@odata.count'] > (pre.tickets.length + data.value.length),
        tickets: [...pre.tickets, ...data.value],
        totalTickets: data['@odata.count'],
      }));
    } catch (error) {
      console.error(error);
      setTicketsState(pre => ({ ...pre, loading: false }));
    }
  };

  const searchItems = useCallback(
    _.debounce((_searchText: string) => {
      setTicketsState(pre => ({ ...pre, pageNumber: 1, tickets: [], searchText: _searchText }));
    }, 2000), []
  );

  const changeFilter = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    setState(state => ({ ...state, request: { isToggleClosed: checked } }));
    setTicketsState(state => ({ ...state, tickets: [], pageNumber: 1 }));
  };

  useEffect(() => {
    loadSideViewTickets(ticketsState.pageNumber, ticketsState.searchText, currentState.request.isToggleClosed);
  }, [ticketsState.pageNumber, ticketsState.searchText, currentState.request.isToggleClosed]);

  return (
    <div className={`h-full p-4`} style={{ borderRight: '1px solid #E4E4EB' }}>
      <div style={{ height: '13%', zIndex: 9 }}>
        <Flex className="mb-2" hAlign="end">
          <Toggle label={t('requests.edit-request-view.show-closed-tickets')} styles={{ label: { color: `${globalTheme.siteVariables.theme === themeNames.Dark ? 'white' : 'black'}` } }} inlineLabel
            onChange={changeFilter} {...(currentState.request.isToggleClosed && { defaultChecked: true, style: { background: 'var(--mgt-theme-brand-foreground1)', opacity: 1 } })} />
        </Flex>
        <Input placeholder={t('requests.edit-request-view.search-for-a-ticket')} icon={<SearchIcon />} iconPosition="end" clearable inverted fluid defaultValue={ticketsState.searchText} onChange={(_, event) => searchItems(event.value ?? '')} />
      </div>
      <div className="overflow-auto mt-2" style={{ height: '83%' }}>
        {ticketsState.tickets.length > 0 && ticketsState.tickets.map((ticket: Ticket, idx: number) => (
          (idx === ticketsState.tickets.length - 1) ?
            <div key={ticket.Id} ref={lastTicketElementRef}><div ref={ticketId === ticket.Id ? myRef : null}><TicketRequestCard key={ticket.Id} ticket={ticket} active={ticketId === ticket.Id} view="edit" /></div></div> :
            <div key={ticket.Id}><div ref={ticketId === ticket.Id ? myRef : null}><TicketRequestCard key={ticket.Id} ticket={ticket} active={ticketId === ticket.Id} view="edit" /></div></div>
        ))}
        {!ticketsState.loading && ticketsState.tickets.length == 0 && (
          <EmptyData SVGIcon={<FlashlightSVG width={110} height={110} className={'mb-4'} />} headerText={t('requests.edit-request-view.there-arent-any-matches')} subheaderText={t('common.try-a-different-search')} />
        )}
        {ticketsState.loading && (
          <Flex column className="side-list-skeleton">
            <RequestCardSkeleton />
            <RequestCardSkeleton />
            <RequestCardSkeleton />
          </Flex>
        )}
      </div>
    </div>
  );
};